
import { tables, supabase } from "@/lib/supabase";
import { handleError, ErrorSeverity, ErrorContext } from "@/utils/errorHandler";
import { Payment } from "../types";
import { PaymentCacheService } from "./paymentCacheService";
import { mapToPayment } from "./paymentUtils";

/**
 * Service for retrieving family payments
 */
export const FamilyPaymentService = {
  /**
   * Get payments for a specific family with pagination support
   */
  getPaymentsForFamily: async (
    familyId: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{data: Payment[], totalCount: number}> => {
    const context: ErrorContext = {
      operation: "getPaymentsForFamily",
      additionalInfo: { familyId, page, pageSize },
    };

    try {
      // Check cache first
      const cacheKey = `family:${familyId}:${page}:${pageSize}`;
      const cachedData = PaymentCacheService.get(cacheKey);
      if (cachedData) {
        return { data: cachedData, totalCount: cachedData.length };
      }

      // Count query for pagination
      const { count, error: countError } = await supabase
        .from('payments')
        .select('*', { count: 'exact', head: true })
        .eq("family_id", familyId);
      
      if (countError) throw countError;
      
      // Calculate pagination values
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, error } = await supabase
        .from('payments')
        .select()
        .eq("family_id", familyId)
        .order("paid_at", { ascending: false })
        .range(from, to);

      if (error) throw error;
      
      const payments = data ? data.map(mapToPayment) : [];
      
      // Cache the result
      PaymentCacheService.set(cacheKey, payments);
      
      return { 
        data: payments, 
        totalCount: count || 0 
      };
    } catch (error) {
      handleError(
        error,
        "Failed to load payments",
        ErrorSeverity.ERROR,
        context
      );
      return { data: [], totalCount: 0 };
    }
  },

  /**
   * Get family payments without using RPC (temporary solution)
   */
  getFamilyPayments: async (): Promise<Payment[]> => {
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*');

      if (error) {
        console.error("Error fetching family payments:", error);
        return [];
      }

      return data ? data.map(mapToPayment) : [];
    } catch (error) {
      console.error("Unexpected error fetching family payments:", error);
      return [];
    }
  },

  /**
   * Get recent payments for a family
   */
  getRecentPayments: async (familyId: string): Promise<Payment[]> => {
    const result = await FamilyPaymentService.getPaymentsForFamily(familyId);
    return result.data;
  }
};
